import { create } from 'zustand';
import { apiUrl } from '../api/apiBaseUrl';
import { useAppVersionStore } from './useAppVersionStore';

export type BackendStatus = 'online' | 'checking' | 'offline';

/** Erst nach so vielen fehlgeschlagenen Proben gilt das Backend als offline. */
const FAILURES_BEFORE_OFFLINE = 2;
const PROBE_TIMEOUT_MS = 5000;
/** Backoff der Proben; der letzte Wert wird danach wiederholt. */
const RETRY_DELAYS_MS = [3000, 5000, 10000, 15000];
/** Wie lange die Erfolgsmeldung nach der Erholung stehen bleibt. */
export const RECOVERY_NOTICE_MS = 4000;

interface BackendStatusState {
  status: BackendStatus;
  /** Zeitpunkt der nächsten automatischen Probe (für den Countdown im Banner). */
  nextRetryAt: number | null;
  /** Zeitpunkt der Erholung — das Banner zeigt danach kurz eine Entwarnung. */
  recoveredAt: number | null;

  /** Aus dem Axios-Interceptor: ein Request scheiterte auf Transport-/Gateway-Ebene. */
  reportFailure: () => void;
  /** Aus dem Axios-Interceptor: ein Request kam durch. */
  reportSuccess: () => void;
  /** „Jetzt erneut versuchen" im Wartungsbanner. */
  probeNow: () => Promise<void>;
}

// Modul-lokaler Schleifen-Zustand: gehört nicht in den Store, weil kein Rendering
// davon abhängt und ein Timer-Handle nichts im State verloren hat.
let retryTimer: number | null = null;
let consecutiveFailures = 0;
let probeInFlight = false;

function clearRetryTimer(): void {
  if (retryTimer !== null) {
    window.clearTimeout(retryTimer);
    retryTimer = null;
  }
}

/**
 * Verfügbarkeits-Probe gegen /api/v1/health.
 *
 * Bewusst rohes fetch statt des generierten Orval-Clients: der Aufruf darf weder durch
 * den eigenen Response-Interceptor laufen (sonst Rekursion in reportFailure) noch den
 * Tenant-Header oder das Auth-Cookie mitschicken. Der Endpoint ist [AllowAnonymous].
 */
async function probeHealth(): Promise<boolean> {
  try {
    const response = await fetch(apiUrl('/api/v1/health'), {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export const useBackendStatusStore = create<BackendStatusState>((set, get) => {
  function scheduleNextProbe(): void {
    const index = Math.min(consecutiveFailures - 1, RETRY_DELAYS_MS.length - 1);
    const delay = RETRY_DELAYS_MS[Math.max(index, 0)];

    clearRetryTimer();
    set({ nextRetryAt: Date.now() + delay });
    retryTimer = window.setTimeout(() => {
      retryTimer = null;
      void runProbe();
    }, delay);
  }

  function markOnline(): void {
    clearRetryTimer();
    // Nur ein tatsächlich angezeigtes Wartungsbanner bekommt eine Entwarnung.
    // Ein 'checking', das nie zu 'offline' wurde, war für den Nutzer unsichtbar.
    const wasDown = get().status === 'offline';
    consecutiveFailures = 0;
    set({
      status: 'online',
      nextRetryAt: null,
      recoveredAt: wasDown ? Date.now() : null,
    });

    if (wasDown) {
      // Nach einem Deployment ist meist auch der Client neu — direkt mitprüfen.
      void useAppVersionStore.getState().check();
    }
  }

  async function runProbe(): Promise<void> {
    if (probeInFlight) return;
    probeInFlight = true;
    try {
      const healthy = await probeHealth();
      if (healthy) {
        markOnline();
        return;
      }

      consecutiveFailures += 1;
      if (consecutiveFailures >= FAILURES_BEFORE_OFFLINE) {
        set({ status: 'offline' });
      }
      scheduleNextProbe();
    } finally {
      probeInFlight = false;
    }
  }

  return {
    status: 'online',
    nextRetryAt: null,
    recoveredAt: null,

    reportFailure: () => {
      // Läuft bereits eine Probe-Schleife? Dann ist alles Nötige in Gang.
      if (get().status !== 'online' || retryTimer !== null || probeInFlight) return;

      consecutiveFailures = 0;
      set({ status: 'checking', recoveredAt: null });
      void runProbe();
    },

    reportSuccess: () => {
      if (get().status === 'online' && consecutiveFailures === 0) return;
      markOnline();
    },

    probeNow: async () => {
      clearRetryTimer();
      set({ nextRetryAt: null });
      await runProbe();
    },
  };
});

export default useBackendStatusStore;
